import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';
import tenantImg from '../Assets/tenant.jpg';
import '../AuthenticationPages/Auth.css';
import './TenantSignUp.css';
import GuideWell_Blue from '../Assets/RSIDE.png';

import { formatPhone } from '../components/formatPhone';

const BASE_URL = 'https://api.rside.app';

function TenantSignUp() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [inviteCode, setInviteCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [signupData, setSignupData] = useState(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const passwordRules = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setCodeError('');
    setCodeLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/TenantApp/GetSignUpinfoByReferenceCode?reference_code=${encodeURIComponent(inviteCode.trim())}`);
      if (!r.ok) { setCodeError('Invalid invite code. Please check and try again.'); return; }
      const data = await r.json();
      setSignupData(data);
      const tenant = data.tenants?.[0];
      if (tenant) {
        setFullName(tenant.Full_Name || '');
        setPhone(formatPhone(tenant.Phone_Number || ''));
        setEmail(tenant.Email_Address || '');
      }
      setStep(2);
    } catch {
      setCodeError('Something went wrong. Please try again.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) { setSubmitError('Password does not meet all requirements.'); return; }
    if (!agreeTerms) { setSubmitError('Please agree to the Terms & Privacy Policy.'); return; }
    setSubmitError('');
    setSubmitLoading(true);
    const tenantId = signupData?.tenants?.[0]?.id;
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      if (tenantId) {
        await fetch(
          `${BASE_URL}/TenantApp/UpdateContactInfo?${new URLSearchParams({ tenant_id: tenantId, email, phone, SignedUpFrom: 'website' })}`,
          { method: 'PUT' }
        );
      }
      navigate('/tenant/home');
    } catch (err) {
      const friendlyErrors = {
        'auth/email-already-in-use': 'An account with this email already exists. Try logging in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
      };
      setSubmitError(friendlyErrors[err.code] || 'Something went wrong. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const BrandPanel = () => (
    <div className="auth-brand-side">
      <h1 className="auth-brand-tagline">Join your community.</h1>
      <p className="auth-brand-sub">Your landlord has invited you. Set up your account to manage rent, requests &amp; more.</p>
      <ul className="auth-brand-features">
        <li><FaCheckCircle className="auth-feature-icon" /> View your lease</li>
        <li><FaCheckCircle className="auth-feature-icon" /> Pay rent online</li>
        <li><FaCheckCircle className="auth-feature-icon" /> Submit maintenance requests</li>
        <li><FaCheckCircle className="auth-feature-icon" /> Get property updates</li>
      </ul>
    </div>
  );

  if (step === 1) {
    return (
      <div className="auth-container auth-bg-full">
        <img src={tenantImg} alt="" className="auth-full-bg-img" />
        <div className="auth-full-overlay" />

        <div className="auth-inner">
        {/* Brand on LEFT, Card on RIGHT — matches manager layout */}
        <BrandPanel />
        <div className="auth-full-card-wrap">
          <div className="auth-card ts-invite-card auth-dark-card">
            <img src={GuideWell_Blue} alt="RSIDE" className="auth-GuideWell_Blue" />

            <div className="ts-invite-body">
              <h2 className="ts-invite-title">Enter your invite code</h2>
              <p className="ts-invite-desc">
                Your landlord sent you a 10-character code.<br />Enter it below to get started.
              </p>

              <form onSubmit={handleRedeemCode} className="auth-form">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Invite Code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    maxLength={12}
                    required
                    className="form-input ts-code-input"
                    autoCapitalize="characters"
                  />
                </div>
                {codeError && (
                  <p style={{ color: '#ef4444', fontSize: 13, margin: 0, textAlign: 'center' }}>{codeError}</p>
                )}
                <button type="submit" className="auth-button" disabled={codeLoading}>
                  {codeLoading ? 'Checking…' : 'Redeem Code'}
                </button>
              </form>
            </div>

            <div className="auth-links">
              <p>Already have an account? <Link to="/tenant/login">Log In</Link></p>
              <p><Link to="/select-role?mode=signup" className="forgot-password">← Back</Link></p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  const addr = signupData;
  const propertyLine1 = addr?.street_address || '';
  const propertyLine2 = addr?.unit_number ? `Unit ${addr.unit_number}` : '';
  const propertyLine3 = [addr?.city, addr?.state, addr?.zipcode].filter(Boolean).join(', ');

  return (
    <div className="auth-container auth-bg-full">
      <img src={tenantImg} alt="" className="auth-full-bg-img" />
      <div className="auth-full-overlay" />

      <div className="auth-inner">
      {/* Brand on LEFT, Card on RIGHT — matches manager layout */}
      <BrandPanel />
      <div className="auth-full-card-wrap ts-confirm-wrap">
      <div className="auth-card signup-card ts-confirm-card auth-dark-card">
        <img src={GuideWell_Blue} alt="RSIDE" className="auth-logo ts-logo" />

        <h2 className="auth-title">Almost there</h2>
        <p className="ts-confirm-sub">Confirm your details to finish setting up your account.</p>

        <div className="ts-property-box">
          <div className="ts-property-label">
            <FaMapMarkerAlt style={{ marginRight: 6 }} />PROPERTY
          </div>
          <div className="ts-property-addr">{propertyLine1}</div>
          {propertyLine2 && <div className="ts-property-addr">{propertyLine2}</div>}
          <div className="ts-property-addr">{propertyLine3}</div>
        </div>

        <form onSubmit={handleCreateAccount} className="auth-form">

          <div className="ts-section-box">
            <p className="ts-section-header">Your Info</p>

            <div className="ts-field">
              <label className="ts-field-label">Full Name</label>
              <input
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="form-input"
              />
            </div>

            <div className="ts-field">
              <label className="ts-field-label">Phone Number</label>
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                required
                className="form-input"
              />
            </div>

            <div className="ts-field">
              <label className="ts-field-label">Email</label>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="ts-section-box">
            <p className="ts-section-header">Security</p>

            <div className="ts-field">
              <label className="ts-field-label">Password</label>
              <div
                className="form-group password-group"
                style={{ margin: 0 }}
                onFocus={() => setShowRules(true)}
                onBlur={() => setShowRules(false)}
              >
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input"
                />
                <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
                {showRules && (
                  <div className="password-rules">
                    <p className={passwordRules.length ? 'valid' : 'invalid'}>• At least 8 characters</p>
                    <p className={passwordRules.upper && passwordRules.lower ? 'valid' : 'invalid'}>• Upper &amp; lowercase letter</p>
                    <p className={passwordRules.number ? 'valid' : 'invalid'}>• One number</p>
                    <p className={passwordRules.special ? 'valid' : 'invalid'}>• One special character</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="checkbox-group ts-terms-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                required
              />
              <span>I agree to the{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="auth-inline-link">Terms</a>
                {' '}&amp;{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="auth-inline-link">Privacy Policy</a>
              </span>
            </label>
          </div>

          {submitError && (
            <p style={{ color: '#ef4444', fontSize: 13, margin: 0, textAlign: 'center' }}>{submitError}</p>
          )}

          <button type="submit" className="auth-button" disabled={submitLoading}>
            {submitLoading ? 'Creating Account…' : 'Create Account'}
          </button>
        </form>
      </div>
      </div>
      </div>

    </div>
  );
}

export default TenantSignUp;
